import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Also send token in Authorization header for resilience against cookie-less browsers
    if (data?.access_token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${data.access_token}`;
      localStorage.setItem("bs_token", data.access_token);
    }
    setUser({ id: data.id, email: data.email, role: data.role });
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    delete api.defaults.headers.common["Authorization"];
    localStorage.removeItem("bs_token");
    setUser(null);
  };

  // Restore token on boot
  useEffect(() => {
    const t = localStorage.getItem("bs_token");
    if (t) api.defaults.headers.common["Authorization"] = `Bearer ${t}`;
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
